// §4.2 gap-fill join flow: `/join?token=...` from an org admin's shared link
// (`OrgSettingsPage`'s Join links section). Unlike `/invite`, there's no target email to match
// — anyone signed in with a verified email may accept, which is the whole point of a link over
// a per-email invite. GET /org-join-links/{token} is public so the preview renders before
// asking the visitor to sign in.
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { JoinLinkPreviewOut, OrgOut } from "../api/types";
import { useSession } from "../session";
import { AuthShell } from "../components/AuthShell";
import { FormNotice, LoadingButton } from "../components/form";

export default function JoinLinkAcceptPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { user, loading: sessionLoading, refresh } = useSession();

  const [preview, setPreview] = useState<JoinLinkPreviewOut | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    api<JoinLinkPreviewOut>("GET", `/org-join-links/${encodeURIComponent(token)}`)
      .then(setPreview)
      .catch((err) => {
        setLoadError(
          err instanceof ApiFail && err.code === "not_found"
            ? "This join link is no longer valid — it may have been revoked."
            : "Couldn't load this link.",
        );
      });
  }, [token]);

  async function onAccept() {
    if (!token) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const org = await api<OrgOut>("POST", "/org-join-links/accept", { token });
      await refresh();
      navigate(`/o/${org.slug}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiFail) {
        setAcceptError(err.code === "email_unverified" ? "Verify your email before joining." : err.message);
      } else {
        setAcceptError("Couldn't reach Grackle. Check your connection and try again.");
      }
    } finally {
      setAccepting(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Join link" subtitle="This link is missing its join token.">
        <FormNotice tone="error">Check the link you were sent and try again.</FormNotice>
      </AuthShell>
    );
  }

  if (loadError) {
    return (
      <AuthShell title="Join link" subtitle="This link couldn't be loaded.">
        <FormNotice tone="error">{loadError}</FormNotice>
      </AuthShell>
    );
  }

  if (!preview || sessionLoading) {
    return (
      <AuthShell title="Join link" subtitle="Loading…">
        <div />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Join ${preview.org_name}`}
      subtitle={`This link adds you as a${preview.role === "admin" ? "n" : ""} ${preview.role}.`}
      footer={
        !user ? (
          <>
            Already have an account? <Link to="/signin">Sign in</Link>, or{" "}
            <Link to="/signup">create one</Link> to continue.
          </>
        ) : undefined
      }
    >
      {!user ? (
        <div className="auth-form">
          <FormNotice>Sign in or create an account, then come back to this link.</FormNotice>
          <LoadingButton block onClick={() => navigate("/signin")}>
            Sign in to continue
          </LoadingButton>
        </div>
      ) : (
        <div className="auth-form">
          {acceptError ? <FormNotice tone="error">{acceptError}</FormNotice> : null}
          <LoadingButton block loading={accepting} onClick={onAccept}>
            {accepting ? "Joining…" : `Join ${preview.org_name}`}
          </LoadingButton>
        </div>
      )}
    </AuthShell>
  );
}
